import { SkyCard, SkyCodeBlock, SkyLink, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import { Link } from "wouter";
import type { DataLocation } from "@shared/types/dataLocation";
import { TABLE_NAME_CASE_DBT_NOTE } from "@shared/types/instances";
import type { Reliability } from "@shared/types/reliability";
import { ErrorState, LoadingRows } from "@/components/access/ScreenStates";
import { LateBadge, ReliabilityHelpLink, lastLoadedText } from "@/components/reliability/Freshness";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useLiveRunRefresh } from "@/hooks/use-live-run-refresh";
import { useReliability } from "@/hooks/use-reliability";
import {
  OpenSnowflakeButton,
  isPrivateLink,
  usableAccountUrl,
} from "@/components/your-data/OpenSnowflakeButton";
import { usePermissions } from "@/hooks/use-permissions";
import { askToSetDestination } from "@/lib/transformations/dbtSettings";
import { ROUTES } from "@/lib/routes";
import { useDataLocation } from "@/hooks/use-data-location";

/**
 * Your data (ADR 0011, 2026-10-08; it replaces the Explorer): where skyData puts this instance's
 * data and how a person opens it in Snowflake with their own account. It shows no table and no
 * row, and skyData reads nothing back from the warehouse for it: the server answers from the
 * binding and skyData's own load records (`GET /api/v2/instances/current/data-location`).
 * Every instance role reads it, a Viewer included (owner decision D1).
 *
 * The example query is built here, not on the server, so the server's absence test for warehouse
 * SQL stays meaningful. The server answers each name as Snowflake names it, with a flag saying
 * whether a statement must quote it, the load's own rule (a name an unquoted one does not fold to,
 * or a reserved word such as `ORDER`), and the query quotes exactly those parts.
 */

type SnowflakeFacts = NonNullable<DataLocation["snowflake"]>;
type Example = NonNullable<DataLocation["example"]>;

const INTRO =
  "skyData loads your data into your own Snowflake account. To look at it, sign in to Snowflake with your own account. skyData does not show your tables or rows here, and it does not read them back.";
/**
 * #933 (owner 2026-10-13): the person runs the example query with their own Snowflake account and
 * role, so the page says what access it needs and whom to ask.
 */
const QUERY_ACCESS_LINE =
  "You need a Snowflake role that can read this database. If the query is refused, ask your Snowflake admin.";

const SERVICE_ACCOUNT_NOTE =
  "skyData loads with its own service account, which Skypoint and your Snowflake admin set up. Its sign-in is never shown here and never used for your own access.";
const NO_DESTINATION = "No destination is set up for this instance yet.";
const DATABRICKS =
  "Your destination is Databricks. skyData does not load into Databricks yet, so nothing has landed. This page will show where your data lands when it does.";

/** An identifier as the query names it: quoted, `"` doubled, when the server says it must be. */
function sqlPart(name: string, quoted: boolean): string {
  return quoted ? `"${name.replaceAll('"', '""')}"` : name;
}

/** The example query: the database, the schema and the table, or a placeholder for each unknown. */
export function exampleQuery(facts: SnowflakeFacts, example: Example | null): string {
  const database = facts.database
    ? sqlPart(facts.database, facts.databaseQuoted)
    : "<database name>";
  const schema = facts.schema ? sqlPart(facts.schema, facts.schemaQuoted) : "<schema name>";
  const table = example ? sqlPart(example.tableName, example.tableQuoted) : "<table name>";
  return `SELECT * FROM ${database}.${schema}.${table} LIMIT 10;`;
}

/** How the Table names row ends, by the destination's Table name case (#2459 adds Lower case). */
const NAMING_CASE_SENTENCE: Readonly<Record<DataLocation["naming"]["case"], string>> = {
  native: ", in capitals.",
  source:
    ". The connection name is in capitals. The schema and table are spelled as your source spells them.",
  lower: ", all in lower case. Put the name in double quotes when you query it.",
};

/**
 * #994: the instance-wide rule, always in full (`shared/warehouse/targetTableName.ts`). One example
 * is one connection's table and never decides the rule: a source without schemas leaves the schema
 * part out, and the row says so beside every example.
 */
export function namingRule(): string {
  return "<connection name>_<source schema>_<source table>";
}

/**
 * The Example row: what a landed table landed as (one still selected and following the naming
 * rule first, #1072), or what a planned table will land as.
 */
export function exampleSentence(example: Example): string {
  const head = `"${example.connectionName}", table ${example.sourceTable},`;
  const tail = example.ownName
    ? ` This table has its own name, set on its connection's Tables tab, so it does not follow the naming rule${
        example.ruleName ? ` (the rule would give ${example.ruleName})` : ""
      }.`
    : "";
  return example.landed
    ? `${head} lands as ${example.tableName}.${tail}`
    : `${head} will land as ${example.tableName} after its first import.${tail}`;
}

/** The fourth step: where to find the database and the schema in Snowsight. */
function browseStep(facts: SnowflakeFacts): string {
  const base = "In the left menu, open Catalog, then Explorer.";
  if (!facts.database) return base;
  if (!facts.schema) return `${base} Open ${facts.database}.`;
  return `${base} Open ${facts.database}, then ${facts.schema}.`;
}

/** What to ask the admin to grant: the schema when it is known, else the database. */
function grantTarget(facts: SnowflakeFacts): string {
  if (facts.database && facts.schema) return `${facts.database}.${facts.schema}`;
  return facts.database ?? "the database skyData loads into";
}

function Fact({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="grid min-w-0 gap-1 sm:grid-cols-[12rem_1fr]">
      <dt>
        <SkyText size={200} color="muted">
          {label}
        </SkyText>
      </dt>
      <dd className="min-w-0 [overflow-wrap:anywhere]" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={`your-data-${id}-title`} className="space-y-2">
      <SkyText as="h2" block id={`your-data-${id}-title`} size={400} weight="semibold">
        {title}
      </SkyText>
      {children}
    </section>
  );
}

/**
 * #828 (Level B): when each Ready connection last loaded, and whether it is late, from skyData's
 * own run records (`GET /instances/current/reliability`). ADR 0011 allows it: it reads no
 * warehouse. Nothing while the read loads or fails: the page's own facts do not depend on it.
 */
function LastLoaded({ reliability }: { reliability: Reliability | undefined }) {
  const zone = useDisplayTz();
  const ready = (reliability?.connections ?? []).filter((c) => c.state === "ready");
  if (!reliability) return null;
  return (
    <Section id="loaded" title="When it last loaded">
      {ready.length === 0 ? (
        <SkyText as="p" block size={300} testId="your-data-loaded-none">
          No connection is ready to import yet.
        </SkyText>
      ) : (
        <ul className="space-y-2" data-testid="your-data-loaded">
          {ready.map((connection) => (
            <li
              key={connection.dataflowId}
              className="flex flex-wrap items-center gap-x-3 gap-y-1"
              data-testid={`your-data-loaded-${connection.displayName}`}
            >
              <SkyText size={300} weight="semibold">
                {connection.displayName}
              </SkyText>
              <SkyText size={300}>{lastLoadedText(connection, zone)}</SkyText>
              <LateBadge
                connection={connection}
                testId={`your-data-late-${connection.displayName}`}
              />
            </li>
          ))}
        </ul>
      )}
      <ReliabilityHelpLink screen="yourData" testId="your-data-reliability-help" />
    </Section>
  );
}

function SnowflakeLocation({
  location,
  facts,
  reliability,
}: {
  location: DataLocation;
  facts: SnowflakeFacts;
  reliability: Reliability | undefined;
}) {
  const zone = useDisplayTz();
  const example = location.example;
  // A connection's Display name is unique in its instance (`dataflows_display_name_key`).
  const exampleConnection = example?.landed
    ? (reliability?.connections.find((c) => c.displayName === example.connectionName) ?? null)
    : null;
  const nameCase = location.naming.case;
  const accountUrl = usableAccountUrl(facts.accountUrl) ? facts.accountUrl : null;

  return (
    <SkyCard appearance="outline" testId="your-data-card">
      <div className="space-y-6 p-2">
        <Section id="where" title="Where your data lands">
          <SkyText as="p" block size={300}>
            {INTRO}
          </SkyText>
          <dl className="space-y-3">
            <Fact label="Snowflake account" testId="your-data-account">
              {accountUrl ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <SkyText size={300} font="monospace">
                    {facts.accountName}
                  </SkyText>
                  <OpenSnowflakeButton accountUrl={accountUrl} />
                  {isPrivateLink(accountUrl) ? (
                    <SkyText size={200} color="muted" block data-testid="your-data-private-link">
                      This address works only on your company&apos;s network.
                    </SkyText>
                  ) : null}
                </div>
              ) : (
                <SkyText size={300}>
                  Ask your Snowflake admin for your account&apos;s address.
                </SkyText>
              )}
            </Fact>
            <Fact label="Database" testId="your-data-database">
              {facts.database ? (
                <SkyText size={300} font="monospace">
                  {facts.database}
                </SkyText>
              ) : (
                <SkyText size={300}>
                  The database set up for skyData (Skypoint can tell you its name)
                </SkyText>
              )}
            </Fact>
            <Fact label="Schema" testId="your-data-schema">
              {facts.schema ? (
                <SkyText size={300} font="monospace">
                  {facts.schema}
                </SkyText>
              ) : (
                <SkyText size={300}>
                  The schema set up for skyData&apos;s service account (Skypoint can tell you its
                  name)
                </SkyText>
              )}
            </Fact>
            <Fact label="Table names" testId="your-data-naming">
              <SkyText size={300}>
                <SkyText font="monospace">{namingRule()}</SkyText>
                {NAMING_CASE_SENTENCE[nameCase]}
                {" The source schema is left out for a source without schemas."}
                {" A table given its own name on its connection's Tables tab keeps that name."}
                {/* #2459: only where some table is pinned to another case than the rule's. */}
                {location.naming.mixedCases
                  ? " A table loaded before the table name case last changed keeps the name it was created with."
                  : null}
              </SkyText>
              {/* #2488: said here too, since a deployment policy can hide the Destination field. */}
              {nameCase === "source" || nameCase === "lower" ? (
                <SkyText as="p" size={200} block testId="your-data-dbt-quoting">
                  {TABLE_NAME_CASE_DBT_NOTE}
                </SkyText>
              ) : null}
            </Fact>
            {example ? (
              <Fact label="Example" testId="your-data-example">
                <SkyText size={300}>{exampleSentence(example)}</SkyText>
                {exampleConnection ? (
                  <span
                    className="mt-1 flex flex-wrap items-center gap-2"
                    data-testid="your-data-example-loaded"
                  >
                    <SkyText size={200} color="muted">
                      {lastLoadedText(exampleConnection, zone)}
                    </SkyText>
                    <LateBadge connection={exampleConnection} testId="your-data-example-late" />
                  </span>
                ) : null}
              </Fact>
            ) : null}
          </dl>
        </Section>

        <Section id="open" title="How to open it">
          <ol className="list-decimal space-y-2 pl-6" data-testid="your-data-steps">
            <li>
              <SkyText size={300}>
                {accountUrl
                  ? "Select Open Snowflake. It opens in a new tab."
                  : "Open Snowflake in your browser."}
              </SkyText>
            </li>
            <li>
              <SkyText size={300}>
                Sign in with your own Snowflake account. If your company uses single sign-on, use
                that. skyData does not sign you in.
              </SkyText>
            </li>
            <li>
              <SkyText size={300}>
                Choose a warehouse your role can use. You pick it at the top of a worksheet.
              </SkyText>
            </li>
            <li>
              <SkyText size={300}>{browseStep(facts)}</SkyText>
            </li>
            <li className="space-y-2">
              <SkyText size={300} block>
                {example
                  ? "To see a few rows, open a worksheet and run:"
                  : "To see a few rows of a table, open a worksheet and run:"}
              </SkyText>
              <SkyCodeBlock
                language="sql"
                code={exampleQuery(facts, example)}
                testId="your-data-query"
              />
              <SkyText size={200} color="muted" block testId="your-data-query-access">
                {QUERY_ACCESS_LINE}
              </SkyText>
            </li>
          </ol>
        </Section>

        <LastLoaded reliability={reliability} />

        <Section id="missing" title="Can't see it?">
          <SkyText as="p" block size={300} data-testid="your-data-missing-name">
            A table given its own name on its connection&apos;s Tables tab does not follow the
            naming rule. Check that tab first for the name it lands as.
          </SkyText>
          <SkyText as="p" block size={300}>
            {`Your Snowflake role decides what you can see. If the database, the schema or the table is missing, ask your Snowflake admin to grant your role access to ${grantTarget(facts)}.`}
          </SkyText>
        </Section>

        <SkyText as="p" block size={200} color="muted" data-testid="your-data-service-account">
          {SERVICE_ACCOUNT_NOTE}
        </SkyText>
      </div>
    </SkyCard>
  );
}

export default function YourDataPage() {
  const { data, isPending, isError, error, refetch } = useDataLocation();
  const reliability = useReliability();
  // #2005: Last loaded and the example table follow a finished run while the page is open: the
  // page holds the tab's live feed, whose terminal frame refreshes both (#1653), and re-reads them
  // every 30 s in case no frame comes (nothing on this page moves, so never faster).
  useLiveRunRefresh({
    active: false,
    poll: () => {
      void reliability.refetch();
      void refetch();
    },
  });
  const { canAny } = usePermissions();

  if (isPending) return <LoadingRows rows={5} columns={2} />;
  if (isError) {
    return (
      <ErrorState
        title="Where your data lands did not load"
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }
  if (data.destination === "snowflake" && data.snowflake) {
    return (
      <SnowflakeLocation location={data} facts={data.snowflake} reliability={reliability.data} />
    );
  }
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      testId="your-data-unavailable"
      title={data.destination === "databricks" ? "Databricks" : "No destination yet"}
      message={
        data.destination === "databricks"
          ? DATABRICKS
          : canAny("instance.binding.write")
            ? NO_DESTINATION
            : `${NO_DESTINATION} ${askToSetDestination(canAny("instance.binding.read"))}`
      }
      actions={
        data.destination !== "databricks" && canAny("instance.binding.write") ? (
          <SkyLink as={Link} href={ROUTES.settingsDestination} testId="your-data-open-destination">
            Open Destination
          </SkyLink>
        ) : undefined
      }
    />
  );
}
